import React from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  ButtonBase,
  Chip,
  alpha,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import PsychologyIcon from "@mui/icons-material/Psychology";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";

const DASHBOARD_OPTIONS = [
  {
    id: "admin",
    title: "Admin",
    subtitle: "System Administration",
    icon: AdminPanelSettingsIcon,
    color: "#7C3AED",
    gradient: "linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)",
  },
  {
    id: "ceo",
    title: "Executive",
    subtitle: "Executive Dashboard",
    icon: BusinessIcon,
    color: "#059669",
    gradient: "linear-gradient(135deg, #059669 0%, #34D399 100%)",
  },
  {
    id: "pcp",
    title: "PCP",
    subtitle: "Clinical Dashboard",
    icon: LocalHospitalIcon,
    color: "#2563EB",
    gradient: "linear-gradient(135deg, #2563EB 0%, #60A5FA 100%)",
  },
  {
    id: "care_manager",
    title: "Care Manager",
    subtitle: "Care Coordination",
    icon: SupportAgentIcon,
    color: "#DC2626",
    gradient: "linear-gradient(135deg, #DC2626 0%, #F87171 100%)",
  },
  {
    id: "behavioral_health",
    title: "BH",
    subtitle: "Behavioral Health",
    icon: PsychologyIcon,
    color: "#7C3AED",
    gradient: "linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)",
  },
];

/**
 * DashboardSwitcher - Selector visual de dashboards para Admin
 */
export default function DashboardSwitcher({ currentDashboard, onChangeDashboard, isPreviewMode }) {
  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        p: 2,
        borderRadius: 4,
        background: (theme) =>
          `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                borderRadius: 2,
                bgcolor: "primary.main",
                color: "primary.contrastText",
              }}
            >
              <VisibilityIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Dashboard Preview
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Switch between dashboard views as Admin
              </Typography>
            </Box>
          </Stack>

          {isPreviewMode && (
            <Chip
              icon={<CloseIcon />}
              label="Exit Preview"
              color="warning"
              variant="filled"
              onClick={() => onChangeDashboard(null)}
              sx={{
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": { bgcolor: "warning.dark" },
              }}
            />
          )}
        </Stack>

        {/* Dashboard Cards */}
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(5, 1fr)",
            },
          }}
        >
          {DASHBOARD_OPTIONS.map((option) => {
            const Icon = option.icon;
            const isActive = currentDashboard === option.id;
            const isAdminHome = option.id === "admin" && !isPreviewMode;

            return (
              <ButtonBase
                key={option.id}
                onClick={() => {
                  if (option.id === "admin") {
                    onChangeDashboard(null);
                  } else {
                    onChangeDashboard(option.id);
                  }
                }}
                sx={{
                  display: "block",
                  textAlign: "left",
                  borderRadius: 3,
                  overflow: "hidden",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  transform: isActive ? "scale(1.02)" : "scale(1)",
                  "&:hover": {
                    transform: "scale(1.02)",
                    "& .card-bg": {
                      opacity: 1,
                    },
                  },
                }}
              >
                <Paper
                  elevation={isActive ? 8 : 1}
                  sx={{
                    p: 2,
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 3,
                    border: "2px solid",
                    borderColor: isActive ? option.color : "transparent",
                    bgcolor: isActive ? alpha(option.color, 0.08) : "background.paper",
                  }}
                >
                  {/* Background gradient on hover/active */}
                  <Box
                    className="card-bg"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background: option.gradient,
                      opacity: isActive ? 0.1 : 0,
                      transition: "opacity 0.3s ease",
                    }}
                  />

                  <Stack spacing={1.5} sx={{ position: "relative" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 44,
                        height: 44,
                        borderRadius: 2.5,
                        background: option.gradient,
                        color: "white",
                        boxShadow: `0 4px 14px ${alpha(option.color, 0.4)}`,
                      }}
                    >
                      <Icon />
                    </Box>

                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{ color: isActive ? option.color : "text.primary" }}
                        >
                          {option.title}
                        </Typography>
                        {isAdminHome && (
                          <Chip
                            label="Home"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.65rem",
                              bgcolor: alpha(option.color, 0.15),
                              color: option.color,
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.25 }}
                      >
                        {option.subtitle}
                      </Typography>
                    </Box>

                    {isActive && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: option.color,
                          boxShadow: `0 0 10px ${option.color}`,
                        }}
                      />
                    )}
                  </Stack>
                </Paper>
              </ButtonBase>
            );
          })}
        </Box>
      </Stack>
    </Paper>
  );
}
